import { type FC, useState } from 'react';

import { Caption, Heading, Stack, Text } from '@eluve/components';

import type { MiniAppContext } from '../../../types';
import { EditFindingModal } from '../EditFindingModal';

import { FindingRow } from './FindingRow';
import { FindingsGroup } from './FindingsGroup';
import { ReviewProgressHeader } from './ReviewProgressHeader';
import { categorize } from './grouping';
import type { PendingFinding } from './types';
import type { useFindingsReview } from './useFindingsReview';

type Review = ReturnType<typeof useFindingsReview>;

export const FindingsReview: FC<{
  context: MiniAppContext;
  review: Review;
  showApply?: boolean;
  onApplied?: () => void;
}> = ({ context, review, showApply = true, onApplied }) => {
  const { tenantId, appointmentId } = context;
  const [editing, setEditing] = useState<PendingFinding | null>(null);

  if (review.findings.length === 0) {
    return <Text color="tertiary">No findings awaiting review.</Text>;
  }

  const busy = review.mutating || review.rerunning;

  return (
    <Stack gap={4}>
      <ReviewProgressHeader
        progress={review.progress}
        allReviewed={review.allReviewed}
        rerunning={review.rerunning}
        busy={busy}
        showApply={showApply}
        onApply={() => {
          review.apply();
          onApplied?.();
        }}
        onRerun={review.rerun}
      />

      <Stack gap={0} className="rounded-lg bg-bg-insights-subtle px-4 py-3">
        <Heading size="lg" weight="semibold">
          Confirm what happened
        </Heading>
        <Text size="sm" color="secondary">
          Conditions, observations, and goals extracted this session.
        </Text>
      </Stack>

      <Stack gap={4}>
        {review.groups.map((group) => {
          const reviewedCount = group.findings.filter(
            (f) => f.decision !== 'pending',
          ).length;
          return (
            <FindingsGroup
              key={group.key}
              group={group}
              reviewedCount={reviewedCount}
              approveAllDisabled={
                busy || reviewedCount === group.findings.length
              }
              onApproveAll={() => review.approveAll(group.findings)}
            >
              {categorize(group.findings).map((category) => (
                <Stack key={category.key} gap={2}>
                  <Caption color="tertiary" className="tracking-wide">
                    {category.label}
                  </Caption>
                  {category.findings.map((finding) => (
                    <FindingRow
                      key={finding.id}
                      finding={finding}
                      disabled={busy}
                      onApprove={() => review.decide(finding, 'approve')}
                      onReject={() => review.decide(finding, 'reject')}
                      onUndo={() => review.undo(finding)}
                      onEdit={() => setEditing(finding)}
                    />
                  ))}
                </Stack>
              ))}
            </FindingsGroup>
          );
        })}
      </Stack>

      {editing && appointmentId ? (
        <EditFindingModal
          tenantId={tenantId}
          appointmentId={appointmentId}
          finding={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            review.reload();
          }}
          onDelete={async () => {
            const deleted = await review.dismiss(editing);
            if (deleted) setEditing(null);
          }}
        />
      ) : null}
    </Stack>
  );
};
